import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useFlagStore } from '@/store/flags/flags'
import { useWhoAmI } from './whoAmI'

/**
 *
 */
export function usePermissions() {
  const { permissions, selfEntity } = useWhoAmI()
  const {
    envAllowsDirectPlaybookAdminEditing,
    overrideUseLlmDevTools,
    overrideViewAllTasks,
    showCareTeamDocViewer,
    showOmaatEnrollmentQueue,
    showScheduledCallsQueue,
    enableInboundFaxes,
    useProviderCallbacks,
    isLoaded: flagsAreLoaded,
  } = storeToRefs(useFlagStore())

  const isLoaded = computed(() => {
    return selfEntity.value !== null && flagsAreLoaded.value
  })

  /**
   *
   * @param permission
   */
  function hasPlaybookAdminPermission(permission: string): boolean {
    return (
      envAllowsDirectPlaybookAdminEditing.value &&
      permissions.value.includes(permission)
    )
  }

  // pages
  const canViewPageAllTasks = computed(
    () =>
      permissions.value.includes('ui-view:page-all-tasks') ||
      overrideViewAllTasks.value
  )
  const canViewPagePodLeadQueue = computed(() =>
    permissions.value.includes('ui-view:page-pod-lead-queue')
  )
  const canViewPageAdminCarePods = computed(() =>
    permissions.value.includes('ui-view:page-admin-care-pods')
  )
  const canViewPathwaysAdmin = computed(() =>
    permissions.value.includes('ui-view:page-admin-pathways')
  )
  const canViewPageEnrollmentLeadQueue = computed(() =>
    permissions.value.includes('ui-view:page-enrollment-specialist-lead-queue')
  )
  const canViewEnrollmentQueue = computed(() =>
    permissions.value.includes('ui-view:page-enrollment-queue')
  )
  const canViewOmaatEnrollmentQueue = computed(
    () => canViewEnrollmentQueue.value && showOmaatEnrollmentQueue.value
  )
  const canViewScheduledCallsQueue = computed(
    () => showScheduledCallsQueue.value
  )
  const canViewProviderCallbacks = computed(
    () => useProviderCallbacks.value
    // && permissions.value.includes('ui-view:page-provider-callbacks') // TODO CT-507
  )
  const canViewPageUnknownTexts = computed(() =>
    permissions.value.includes('ui-view:page-unknown-texts')
  )
  const canViewPageInboundFaxes = computed(
    () =>
      enableInboundFaxes.value &&
      permissions.value.includes('ui-view:page-inbound-faxes')
  )
  const canViewPageFormResponses = computed(() =>
    permissions.value.includes('ui-view:page-form-responses')
  )
  const canViewPageNashDebugSandbox = computed(() =>
    permissions.value.includes('ui-view:page-nash-debug-sandbox')
  )
  const canViewAdminUserQueue = computed(() =>
    permissions.value.includes('ui-view:page-admin-user-queue')
  )
  const canViewCareTeamDocViewer = computed(() => showCareTeamDocViewer.value)

  // catchall
  const canEditDocumentCategories = computed(() =>
    permissions.value.includes('edit:documents:document-categories')
  )
  const canEditCareTeamDocuments = computed(() =>
    permissions.value.includes('edit:documents:care-team-documents')
  )

  // emr
  const canEditEmrExpressConfig = computed(() =>
    permissions.value.includes('edit:emr:express-config')
  )

  // patients
  const canEditPatientTreatmentStatus = computed(() =>
    permissions.value.includes('edit:patients:treatment-status')
  )
  const canEditPatientAcuityScore = computed(() =>
    permissions.value.includes('edit:patients:acuity-score')
  )
  const canEditPatientDiagnoses = computed(() =>
    permissions.value.includes('edit:patients:diagnoses')
  )
  const canEditPatientComorbidities = computed(() =>
    permissions.value.includes('edit:patients:comorbidities')
  )
  const canEditDomains = computed(() =>
    permissions.value.includes('edit:patients:domains')
  )
  const canEditPatientContract = computed(() =>
    permissions.value.includes('edit:patients:contract')
  )
  const canEditPatientMedicalIncapacity = computed(() =>
    permissions.value.includes('edit:patients:medical-incapacity')
  )
  const canEditPatientIsTestPatient = computed(() =>
    permissions.value.includes('edit:patients:is-test')
  )
  const canEditPatientMeosCharges = computed(() =>
    permissions.value.includes('edit:patients:meos-charges')
  )

  // patients admin
  const canEditPatientAdminComorbidities = computed(() =>
    permissions.value.includes('edit:patients:comorbidity-references')
  )

  // pathways
  const canEditPathwaysPriority = computed(() =>
    permissions.value.includes('edit:pathways:priority')
  )
  const canEditBulkSubtasks = computed(() =>
    permissions.value.includes('power:pathways:bulk-edit')
  )
  const canExtendReattempt = computed(() =>
    permissions.value.includes('power:pathways:extended-reattempt')
  )
  const canOverridePathwaysLimit = computed(() =>
    permissions.value.includes('power:pathways:override-max-amount')
  )
  const canOverridePathwaysBlockedStatus = computed(() =>
    permissions.value.includes('power:pathways:override-blocked-status')
  )
  const canReadTriggerOutcomes = computed(() =>
    permissions.value.includes('read:pathways:trigger-outcomes')
  )
  const canManuallyLinkFormResponseToSubtask = computed(() =>
    permissions.value.includes('power:pathways:link-form-response-subtask')
  )

  // pathways admin
  const canEditPathwaysAdminSubtaskDetails = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-subtask-details')
  )
  const canEditPathwaysAdminSubtaskComposition = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-subtask-composition')
  )
  const canEditPathwaysAdminTaskDetails = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-task-details')
  )
  const canEditPathwaysAdminTaskComposition = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-task-composition')
  )
  const canEditPathwaysAdminPathwayDetails = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-pathway-details')
  )
  const canEditPathwaysTemplateStatus = computed(() =>
    permissions.value.includes('edit:pathways:admin-pathway-composition')
  )
  const canEditPathwaysAdminPathwayComposition = computed(() =>
    hasPlaybookAdminPermission('edit:pathways:admin-pathway-composition')
  )
  const canImportPathways = computed(() =>
    permissions.value.includes('power:pathways:import-pathways')
  )
  const canExportPathways = computed(() =>
    permissions.value.includes('power:pathways:export-pathways')
  )
  const canImportAutotriggers = computed(() =>
    permissions.value.includes('power:pathways:import-autotriggers')
  )
  const canExportAutotriggers = computed(() =>
    permissions.value.includes('power:pathways:export-autotriggers')
  )
  const canEditAdminCarePlanGoalReferences = computed(() =>
    permissions.value.includes('edit:pathways:care-plan-goal-references')
  )

  // communications
  const canSendBulkTexts = computed(() =>
    permissions.value.includes('power:communications:bulk-texts')
  )
  const canCreateCohorts = computed(() =>
    permissions.value.includes('edit:communications:cohorts')
  )
  const canEditOutboundGeorouting = computed(() =>
    permissions.value.includes('edit:communications:outbound-georouting')
  )
  const hasDeleteCommunicationPermission = computed(() =>
    permissions.value.includes('delete:communications:communications')
  )
  const canScheduleCallsOnHolidays = computed(() =>
    permissions.value.includes('power:communications:schedule-on-holiday')
  )
  const canViewZoomCallData = computed(() =>
    permissions.value.includes('read:communications:zoom-call-transcripts')
  )
  const canViewAwsContactCenterData = computed(() =>
    permissions.value.includes('read:communications:aws-contact-center-data')
  )

  // entities
  const canEditStaffRoles = computed(() =>
    permissions.value.includes('edit:entities:admin-entity-roles')
  )

  // linked subtasks
  const canLinkCompletedSubtasks = computed(() =>
    permissions.value.includes('ui-view:page-link-completed-subtasks')
  )

  // llm
  const canUseLlmDevTools = computed(
    () =>
      permissions.value.includes('power:llm:use-dev-tools') ||
      overrideUseLlmDevTools.value
  )

  return {
    isLoaded,
    canViewPageAllTasks,
    canViewPagePodLeadQueue,
    canViewPageAdminCarePods,
    canViewPathwaysAdmin,
    canViewPageEnrollmentLeadQueue,
    canViewEnrollmentQueue,
    canViewPageUnknownTexts,
    canViewPageInboundFaxes,
    canViewPageFormResponses,
    canViewPageNashDebugSandbox,
    canViewCareTeamDocViewer,
    canViewAdminUserQueue,
    canEditDocumentCategories,
    canEditCareTeamDocuments,
    canEditEmrExpressConfig,
    canEditPatientTreatmentStatus,
    canEditPatientAcuityScore,
    canEditPatientDiagnoses,
    canEditPatientComorbidities,
    canEditDomains,
    canEditPatientContract,
    canEditPatientMedicalIncapacity,
    canEditPatientIsTestPatient,
    canEditPatientMeosCharges,
    canEditPatientAdminComorbidities,
    canEditPathwaysPriority,
    canEditBulkSubtasks,
    canExtendReattempt,
    canOverridePathwaysLimit,
    canOverridePathwaysBlockedStatus,
    canReadTriggerOutcomes,
    canManuallyLinkFormResponseToSubtask,
    canEditPathwaysAdminSubtaskDetails,
    canEditPathwaysAdminSubtaskComposition,
    canEditPathwaysAdminTaskDetails,
    canEditPathwaysAdminTaskComposition,
    canEditPathwaysAdminPathwayDetails,
    canEditPathwaysTemplateStatus,
    canEditPathwaysAdminPathwayComposition,
    canImportPathways,
    canExportPathways,
    canImportAutotriggers,
    canExportAutotriggers,
    canEditAdminCarePlanGoalReferences,
    canSendBulkTexts,
    canCreateCohorts,
    canEditOutboundGeorouting,
    hasDeleteCommunicationPermission,
    canScheduleCallsOnHolidays,
    canViewZoomCallData,
    canViewAwsContactCenterData,
    canEditStaffRoles,
    canUseLlmDevTools,
    canLinkCompletedSubtasks,
    canViewOmaatEnrollmentQueue,
    canViewScheduledCallsQueue,
    canViewProviderCallbacks,
  }
}

export type PermissionCheck = Exclude<
  keyof ReturnType<typeof usePermissions>,
  'isLoaded'
>
